//changed due to package upgrade
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Card,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import SignUp from './SignUp';
import TeamConfigure from './TeamConfigure';
import Payment from './Payment';
import {
  initialState,
  getSavedProgress,
  getAuthenticatedProgressUserId,
  getSaveProgressUserId,
  saveProgress,
} from './handleState';
import PurchaseOptions from './PurchaseOptions';
import jwtAxios from '@projectK/services/auth/JWT';
import DOMPurify from 'dompurify';
import DivisionAvailabilityCard from './DivisionAvailabilityCard';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import { formatDateRange } from '@projectK/modules/apps/DateFormat';
const TournamentView = ({
  tournament,
  divisions,
  user,
  showDetails,
  handleRegister,
  handleBack,
  activeStep,
  setActiveStep,
  setIsPaymentOptionSkipped,
}) => {//NOSONAR
  const { tournamentSlug } = useParams();
  const isProductLimit = tournament?.purchase_limit
    ? tournament.purchased_count >= tournament.purchase_limit
    : false;
  const [formData, setFormData] = useState(initialState);
  const [skipPurchaseStep, setSkipPurchaseStep] = useState(false);
  const [registrationWizardSettings, setRegistrationWizardSettings] =
    useState(null);

  const isTeamConfigComplete = (data) => {
    const teamConfig = data?.teamConfig || {};
    const teams = teamConfig?.teams || [];
    if (!teamConfig?.organization_name || !teams.length) return false;

    const coachFullNameRequired =
      registrationWizardSettings?.coach_full_name_required ?? true;
    const coachEmailRequired =
      registrationWizardSettings?.coach_email_required ?? true;
    const coachPhoneRequired =
      registrationWizardSettings?.coach_phone_required ?? true;

    return teams.every(
      (team) =>
        team?.team_name &&
        team?.division_id &&
        (!coachFullNameRequired || team?.coach_name) &&
        (!coachEmailRequired || team?.coach_email) &&
        (!coachPhoneRequired || team?.coach_phone),
    );
  };

  useEffect(() => {
    const fetchRegistrationWizardSettings = async () => {
      try {
        const res = await jwtAxios.get(
          user?.id
            ? `/tournament-registration-wizard-settings/`
            : `/public/tournament-registration-wizard-settings`,
        );
        setRegistrationWizardSettings(res?.data || null);
      } catch (error) {
        // Wizard labels should still work even if settings are missing.
        setRegistrationWizardSettings(null);
      }
    };

    fetchRegistrationWizardSettings();
  }, []);

  useEffect(() => {
    const progressUserId = getAuthenticatedProgressUserId(user);

    if (!progressUserId) {
      // Logged out: always start at Sign Up; ignore stale guest progress.
      setActiveStep(0);
      setFormData(initialState);
      setSkipPurchaseStep(false);
      return;
    }

    const { step, data } = getSavedProgress(tournamentSlug, progressUserId);
    const hasCompleteTeamConfig = isTeamConfigComplete(data);
    const isInvalidAdvancedStep = step > 1 && !hasCompleteTeamConfig;

    if (isInvalidAdvancedStep) {
      // Prevent stale progress from skipping to purchase/payment after refresh.
      setActiveStep(1);
      saveProgress(
        tournamentSlug,
        1,
        {
          purchaseOptions: {},
          payment: {},
        },
        progressUserId,
      );
      setSkipPurchaseStep(false);
    } else if (!step) {
      setActiveStep(1);
    } else {
      setActiveStep(step);
      if (step === 3 && data?.payment?.length && !data?.purchaseOptions?.length) {
        setSkipPurchaseStep(true);
        setIsPaymentOptionSkipped(true);
      }
    }
    setFormData(data);
  }, [tournamentSlug, registrationWizardSettings, user?.id]);

  const checkAndSkipPurchaseStep = async (teams, updatedFormData) => {
    if (!teams?.length) return false;

    let shouldShowStep = false;
    const paymentData = [];

    for (let team of teams) {
      const res = await jwtAxios.get(
        user?.id
          ? `/tournament-purchase-options/purchase-option/by-division/${team?.division_id}`
          : `/public/tournament-purchase-options/purchase-option/by-division/${team?.division_id}`,
        { params: { filters: { is_expired: false } } },
      );

      const options = res?.data?.list || [];
      if (options.length > 1) shouldShowStep = true;
      if (options.length === 1) {
        paymentData.push({
          ...team,
          ...options[0],
          quantity: 1,
          item_price: options[0].purchase_price,
          tournament_org_id: updatedFormData?.orgId,
        });
      }
    }

    if (!shouldShowStep && paymentData.length) {
      const newFormData = { ...updatedFormData, payment: paymentData };
      setFormData(newFormData);
      saveProgress(
        tournamentSlug,
        2,
        { payment: paymentData },
        getSaveProgressUserId(user),
      );
      setSkipPurchaseStep(true);
      setActiveStep(3);
      setIsPaymentOptionSkipped(true);
      return true; // skipped
    }

    return false; // not skipped
  };

  const handleNext = async (stepData) => {
    const updatedFormData = {
      ...formData,
      ...(activeStep === 0 && { signUp: stepData }),
      ...(activeStep === 1 && { teamConfig: stepData }),
      ...(activeStep === 2 && { purchaseOptions: stepData }),
      ...(activeStep === 3 && { payment: stepData }),
    };
    setFormData(updatedFormData);

    // Directly check skipPurchaseStep here
    if (activeStep === 1) {
      const skipped = await checkAndSkipPurchaseStep(
        stepData.teams,
        updatedFormData,
      );
      if (!skipped) {
        setActiveStep(activeStep + 1);
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const rawDescription = tournament?.description;
  const hasDescription =
    typeof rawDescription === 'string' && rawDescription.trim().length > 0;
  const normalizedDescription = hasDescription
    ? rawDescription
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        // Plain text pasted into the editor can include newline-only formatting.
        // Convert to <br> so the detail page preserves intended line breaks.
        .replace(/\r\n|\r|\n/g, '<br />')
    : '<p>No description found</p>';
  const logoUrl = tournament?.tournament_logo
    ? `${process.env.NX_CLOUD_FRONT_URL}/${tournament?.tournament_logo}`
    : null;
  const locationText =
    [tournament?.location_name, tournament?.address]
      .filter(Boolean)
      .join(' - ') || 'Location TBD';
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <SignUp
            onNext={handleNext}
            onBack={handleBack}
            initialValues={formData?.signUp}
          />
        );
      case 1:
        return (
          <TeamConfigure
            onNext={handleNext}
            onBack={handleBack}
            divisions={divisions}
            wizardSettings={registrationWizardSettings}
            initialValues={formData?.teamConfig}
          />
        );
      case 2:
        if (skipPurchaseStep) return renderStepContent(3); // skip step
        return <PurchaseOptions onBack={handleBack} onNext={handleNext} />;
      case 3:
        return (
          <Payment
            tournament={tournament}
            formData={formData}
            onNext={handleNext}
            onBack={handleBack}
            initialValues={formData?.payment}
            wizardSettings={registrationWizardSettings}
          />
        );
      default:
        return null;
    }
  };

  const tournamentStartingPrice = divisions?.reduce((min, division) => {
    const rawPrice = division?.starting_from_price;
    if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
      return min;
    }
    const nextPrice = Number(rawPrice);
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      return min;
    }
    return min === null || nextPrice < min ? nextPrice : min;
  }, null);
  const totalCapacity = divisions?.reduce((sum, division) => {
    const maxTeams = Number(division?.max_teams);
    return sum + (Number.isFinite(maxTeams) ? maxTeams : 0);
  }, 0);
  const totalRemaining = divisions?.reduce((sum, division) => {
    const remainingSpots = Number(division?.remaining_spots);
    return sum + (Number.isFinite(remainingSpots) ? remainingSpots : 0);
  }, 0);
  const divisionsAvailable = Array.isArray(divisions)
    ? divisions.filter((division) => division?.availability_status !== 'full')
        .length
    : 0;
  const lowSpotDivisions = divisions?.filter(
    (division) => division?.availability_status === 'limited',
  ).length;
  const isRegistationClosed =
    divisionsAvailable === 0 ||
    totalRemaining === 0 ||
    tournamentStartingPrice === null;
  return (
    <Box sx={{ width: '100%', p: { xs: 0, sm: 3 } }}>
      {showDetails ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                overflow: 'hidden', // important for clean sections
              }}
            >
              <Grid container spacing={2}>
                <Grid
                  size={{ xs: 12, md: 8 }}
                  sx={{ borderRight: { md: '1px solid #E5E7EB' } }}
                >
                  {/* ================= TOP SECTION ================= */}
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2} alignItems='flex-start'>
                      {/* LOGO */}
                      <Grid size={{ xs: 12, sm: 2, md: 1.5 }}>
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: 96,
                            height: 100,
                            borderRadius: 1.5,
                            backgroundColor: '#F3F4F6',
                            border: '1px solid #E5E7EB',
                            overflow: 'hidden',
                          }}
                        >
                          {logoUrl && (
                            <img
                              src={logoUrl}
                              alt='Tournament Logo'
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          )}
                        </Box>
                      </Grid>

                      {/* TITLE + DETAILS */}
                      <Grid size={{ xs: 12, sm: 10, md: 10.5 }}>
                        <Chip
                          label='Tournament'
                          size='small'
                          sx={{
                            mb: 0.8,
                            backgroundColor: '#E6FFFA',
                            color: '#0F766E',
                            fontWeight: 700,
                          }}
                        />

                        <Typography
                          variant='h2'
                          sx={{ mb: 0.75, fontWeight: 700 }}
                        >
                          {tournament.tournament_name}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Typography sx={{ display: 'flex', gap: 1 }}>
                            <CalendarMonthOutlinedIcon fontSize='small' />
                            {formatDateRange(tournament?.start_date, tournament?.end_date)}
                          </Typography>

                          <Typography sx={{ display: 'flex', gap: 1 }}>
                            <RoomOutlinedIcon fontSize='small' />
                            {locationText}
                          </Typography>
                           <Typography sx={{ display: 'flex', gap: 1 }}>
                            <Person2OutlinedIcon fontSize='small' />
                            {tournament.age_range}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {/* <Box sx={{ borderTop: '1px solid #E5E7EB', my: 2 }} /> */}
                  <Divider />
                  {/* LEFT SIDE (DESCRIPTION) */}
                  <Box sx={{ m: 3 }}>
                    <Typography variant='overline'>About</Typography>

                    <Box
                      sx={{
                        mt: 1,
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        '& p': {
                          margin: '0 0 8px',
                        },
                        '& ul, & ol': {
                          margin: '8px 0',
                          paddingLeft: '20px',
                        },
                        '& li': {
                          margin: '4px 0',
                        },
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(normalizedDescription),
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      borderTop: { xs: '1px solid #E5E7EB', md: 'none' },
                      m: 6,
                      height: '100%',
                    }}
                  >
                    <Card
                      sx={{
                        background: '#f4f5f8',
                        border: 1,
                        borderColor: '#e3e5ec',
                      }}
                    >
                      <Box
                        sx={{
                          pl: { xs: 2, md: 2 },
                          pr: { xs: 2, md: 2 },
                          pt: { xs: 2, md: 4 },
                          pb: { xs: 2, md: 4 },
                        }}
                      >
                        <Box
                          sx={{
                            mb: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: {
                                xs: '25px !important',
                                md: '30px !important',
                              },
                              fontWeight: 800,
                              color: '#111827',
                              pb: 0
                            }}
                          >
                            {tournamentStartingPrice !== null
                              ? `$${tournamentStartingPrice.toFixed(2)}`
                              : 'N/A'}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: '#6B7280',
                            }}
                          >
                            per team entry
                          </Typography>
                        </Box>
                        <Divider />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.8,
                          }}
                        >
                          <Typography>Divisions available:</Typography>

                          <Typography sx={{ mb: 2 }}>
                            <strong>
                              {divisionsAvailable} of {divisions?.length}
                            </strong>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                          }}
                        >
                          <Typography variant='h5'>Spots Remaining:</Typography>
                          <Typography
                            variant='h5'
                            sx={{ fontWeight: 700, mb: 2 }}
                          >
                            {totalRemaining} of {totalCapacity}
                          </Typography>
                        </Box>

                        <Button
                          fullWidth
                          variant='contained'
                          sx={{ mt: 1.5, mb: 2 }}
                          onClick={handleRegister}
                          disabled={isProductLimit || isRegistationClosed}
                        >
                          Register your team
                        </Button>
                        <Typography
                          variant='h5'
                          sx={{
                            mb: 2,
                            textAlign: 'center',
                            color: '#4B5563',
                          }}
                        >
                          Payment due at registration
                        </Typography>
                        {lowSpotDivisions > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              px: 1.5,
                              py: 0.8,
                              borderRadius: 2,
                              backgroundColor: '#FEF3C7',
                              border: '1px solid #FCD34D',
                              color: '#92400E',
                              fontWeight: 700,
                              fontSize: 14,
                              textAlign: 'center',
                            }}
                          >
                            {lowSpotDivisions} division{lowSpotDivisions > 1 ? 's': ''} filling fast
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Box>
                </Grid>
              </Grid>
              <Divider />

              {/* ================= BOTTOM (DIVISIONS) ================= */}
              <Box sx={{ p: 3 }}>
                <Typography variant='h2' sx={{ mb: 2, mt: 2 }}>
                  Available divisions
                </Typography>

                <Grid container spacing={2}>
                  {divisions.length > 0 ? (
                    divisions?.map((division) => (
                      <Grid key={division.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <DivisionAvailabilityCard division={division} />
                      </Grid>
                    ))
                  ) : (
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                      <Box
                        sx={{
                          border: '1px solid #E5E7EB',
                          borderRadius: 2,
                          p: 2,
                        }}
                      >
                        {/* Row 1: name + status */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#EF4444',
                            mb: 2,
                            mt: 2,
                          }}
                        >
                          <Typography variant='h3' sx={{ fontWeight: 700 }}>
                            No division are available
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              flexWrap: 'wrap', // allow wrapping on smaller screens
              '& .MuiStep-root': {
                minWidth: { xs: '100%', sm: 'auto' }, // full width step on xs
                flex: { xs: '0 0 100%', sm: '1' }, // take full width on xs
              },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '1rem' }, // responsive label text
                whiteSpace: 'nowrap', // prevent label from breaking
              },
              overflowX: { xs: 'auto', sm: 'visible' }, // scrollable on mobile
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row' }, // row on all screens
              gap: 2,
            }}
          >
            {[
              'Sign Up',
              `${
                registrationWizardSettings?.team_label || 'Team'
              } Configuration`,
              'Purchase Options',
              'Payment',
            ].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent(activeStep)}
        </>
      )}
    </Box>
  );
};

export default TournamentView;

TournamentView.propTypes = {
  tournament: PropTypes.object,
  divisions: PropTypes.array,
  user: PropTypes.isRequired,
  showDetails: PropTypes.isRequired,
  handleRegister: PropTypes.isRequired,
  handleBack: PropTypes.isRequired,
  activeStep: PropTypes.isRequired,
  setActiveStep: PropTypes.isRequired,
  setIsPaymentOptionSkipped: PropTypes.func,
};
